import Close from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Link,
	Radio,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { useGetMemberListQuery } from '@/api/member-management/admissionRequest';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useNavigate } from 'react-router-dom';
import MemberStatusChip from '@/common/components/MemberStatusChip';

interface Props {
	getSelectMemberRow: any;
	/** The applicant's member id, when the applicant is already a member. */
	pirApplicantId?: string;
	/** The picked member, shown in place of the select button. */
	selectedMember?: {
		id: string;
		fileNo?: string;
		nameEn?: string;
		nameZh?: string;
	} | null;
}

const SelectMemberDialog = (props: Props) => {
	const { getSelectMemberRow, pirApplicantId, selectedMember } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const showModal = useModal();
	const [selectedRowId, setSelectedRowId] = useState<number | string | null>(
		null
	);
	const [selectedRow, setSelectedRow] = useState<any>({});
	const navigate = useNavigate();

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({
		defaultValues,
	});
	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	// Reset to first page whenever the search string changes.
	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	useEffect(() => {
		if (open) {
			setSelectedRowId(null);
			setSelectedRow({});
			methods.reset(defaultValues);
		}
	}, [open]);

	const { data: memberList, isFetching: isMemberListFetching } =
		useGetMemberListQuery(
			{
				type: 0,
				sortings: [],
				pagination: watchPagination,
				searchString: watchSearchString,
				// CHA-006 §7.4: every in-service member, except the applicant.
				// pirApplicantId also hides All services closed; otherwise filter it out here.
				...(pirApplicantId
					? { pirApplicantId }
					: { statusList: [0, 1, 2, 3, 4, 5, 6, 7, 8] }),
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit = async (data: any) => {
		// const parts = nextFileNo?.data.split('/');
		// const response = await registerCase({
		// 	...data,
		// 	admissionDate: data.admissionDate,
		// 	dateOfCaseOpen: data.dateOfCaseOpen,
		// 	id: admissionRequestId,
		// 	fileNo: `${parts[0]}/${parts[1]}/${data.fileNoB}/${data.fileNoC}`,
		// });
		// if ('data' in response) {
		// 	enqueueSnackbar(t('snackbar.updateMemberSuccess'), {
		// 		variant: 'success',
		// 		anchorOrigin: { horizontal: 'center', vertical: 'top' },
		// 		autoHideDuration: 3000,
		// 		action: (key) => {
		// 			return <CloseIcon onClick={() => closeSnackbar(key)} />;
		// 		},
		// 	});
		// 	handleClose();
		// }
		if (!selectedRowId) return;
		getSelectMemberRow(selectedRow);
		handleClose();
	};

	const handleDialog = async () => {
		setOpen(true);
	};

	const columns: GridColDef[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={params.row.id === selectedRowId}
					onChange={() => {
						setSelectedRowId(params.row.id);
						setSelectedRow(params.row);
					}}
				/>
			),
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 25,
		},

		{
			field: 'serviceFollowDesc',
			headerName: t('member.serviceFollow'),
			flex: 15,
		},
		{
			field: 'membershipStateDesc',
			headerName: t('member.membershipState'),
			flex: 15,
		},
		{
			field: 'status',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
	];

	return (
		<>
			{/* Same pattern as CHA-004 PreviousFamilyMemberBanner. */}
			{selectedMember ? (
				<Alert
					severity='info'
					sx={{ bgcolor: '#EAF4FB', alignItems: 'center' }}
					action={
						<Button
							variant='outlined'
							size='small'
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
							onClick={() => handleDialog()}
						>
							{t('family.changeMember')}
						</Button>
					}
				>
					<Trans
						i18nKey='family.selectedMember'
						values={{
							fileNo: selectedMember.fileNo,
							name: [selectedMember.nameEn, selectedMember.nameZh]
								.filter(Boolean)
								.join(' '),
						}}
						components={{
							anchor: (
								<Link
									href={`/member-management/member-list/detail/${selectedMember.id}`}
									target='_blank'
									rel='noopener noreferrer'
									color='#1BBED7'
								/>
							),
						}}
					/>
				</Alert>
			) : (
				<Button variant='outlined' onClick={() => handleDialog()}>
					{t('family.selectMember')}
				</Button>
			)}
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>
					{t('family.selectPersonRecovery')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('family.selectPersonRecoveryText')}
					</Typography>
					<FormProvider {...methods}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
								/>
							</Grid>
							<Grid size={12}>
								<AppDataGrid
									loading={isMemberListFetching}
									rows={memberList?.data.items || []}
									rowCount={memberList?.data.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
									control={methods.control}
								/>
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={!selectedRowId}
						onClick={onSubmit}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SelectMemberDialog;
